import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { money, dateFmt, daysUntil, HEALTH_DOT } from "@/lib/format";
import { pushRecent } from "@/components/CommandPalette";
import { FormDialog } from "@/components/common/FormDialog";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Link2, Inbox, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ACCOUNT_TIERS = ["Growth", "Maintain", "Key", "Strategic"];
const HEALTH_LABEL = { green: "Healthy", amber: "At Risk", red: "Critical" };

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: client, isLoading } = useQuery({ queryKey: ["client", id], queryFn: () => get(`/crm/clients/${id}`) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const inval = () => qc.invalidateQueries({ queryKey: ["client", id] });
  const addIssue = useMutation({ mutationFn: (b) => post(`/crm/clients/${id}/issues`, b), onSuccess: () => { inval(); toast.success("Issue logged"); } });
  const addRequest = useMutation({ mutationFn: (b) => post(`/crm/clients/${id}/requests`, b), onSuccess: () => { inval(); toast.success("Request added"); } });
  const renewAmc = useMutation({ mutationFn: (b) => post(`/crm/clients/${id}/amc/renew`, b), onSuccess: () => { inval(); toast.success("AMC renewed"); } });
  const updateClient = useMutation({ mutationFn: (b) => patch(`/crm/clients/${id}`, b), onSuccess: () => { inval(); toast.success("Updated"); } });
  const addGovMeeting = useMutation({ mutationFn: (b) => post(`/crm/clients/${id}/governance-meetings`, b), onSuccess: () => { inval(); toast.success("Meeting logged"); } });
  const deleteGovMeeting = useMutation({ mutationFn: (gid) => del(`/crm/governance-meetings/${gid}`), onSuccess: () => { inval(); toast.success("Meeting deleted"); } });

  useEffect(() => { if (client) pushRecent({ title: client.name, path: `/crm/clients/${id}` }); }, [client, id]);
  if (isLoading || !client) return <Skeleton className="h-96" />;

  const amc = client.amc || {};
  const renewDays = daysUntil(amc.end_date);
  const renewFam = renewDays == null ? "slate" : renewDays > 60 ? "green" : renewDays > 30 ? "amber" : "red";

  return (
    <div>
      <div className="mb-2 text-xs text-muted-foreground">
        <button onClick={() => navigate("/crm/clients")} className="hover:underline">Clients</button> / {client.name}
      </div>
      <div className="mb-6 flex items-center gap-2">
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">{client.name}</h1>
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="client-health-dot" className={cn("h-3 w-3 rounded-full", HEALTH_DOT[client.health] || "bg-slate-400")} />
          </PopoverTrigger>
          <PopoverContent className="w-64 text-sm">
            <div className="mb-2 font-semibold">{HEALTH_LABEL[client.health] || "Unknown"}</div>
            <dl className="space-y-1 text-xs text-muted-foreground">
              <div className="flex justify-between"><dt>AMC Status</dt><dd className="capitalize">{amc.status || "none"}</dd></div>
              <div className="flex justify-between"><dt>Open Issues</dt><dd>{(client.issues || []).filter((i) => !["Resolved", "Closed"].includes(i.status)).length}</dd></div>
              <div className="flex justify-between"><dt>CSAT Rating</dt><dd>{client.csat_rating ?? "—"}</dd></div>
            </dl>
          </PopoverContent>
        </Popover>
      </div>

      <Tabs defaultValue="overview">
        <TabsList data-testid="client-tabs">
          <TabsTrigger value="overview" data-testid="ct-overview">Overview</TabsTrigger>
          <TabsTrigger value="issues" data-testid="ct-issues">Issues</TabsTrigger>
          <TabsTrigger value="amc" data-testid="ct-amc">AMC</TabsTrigger>
          <TabsTrigger value="requests" data-testid="ct-requests">Client Requests</TabsTrigger>
          {["Strategic", "Key"].includes(client.account_tier) && (
            <TabsTrigger value="governance" data-testid="ct-governance">Governance</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="mt-4 grid grid-cols-2 gap-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="font-display text-sm font-semibold">Account Summary</h3>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Account Owner</dt><dd className="flex items-center gap-1.5"><Avatar user={umap[client.account_owner_id]} size="xs" />{umap[client.account_owner_id]?.name}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Industry</dt><dd className="capitalize">{client.industry}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Converted</dt><dd>{dateFmt(client.converted_at)}</dd></div>
              <div className="flex items-center justify-between"><dt className="text-muted-foreground">Account Tier</dt>
                <dd>
                  <Select value={client.account_tier || "none"} onValueChange={(v) => updateClient.mutate({ account_tier: v === "none" ? null : v })}>
                    <SelectTrigger className="h-7 w-32" data-testid="account-tier-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unset</SelectItem>
                      {ACCOUNT_TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </dd>
              </div>
              <div className="flex items-center justify-between"><dt className="text-muted-foreground">CSAT Rating</dt>
                <dd>
                  <Select value={client.csat_rating != null ? String(client.csat_rating) : "none"}
                    onValueChange={(v) => updateClient.mutate({ csat_rating: v === "none" ? null : Number(v) })}>
                    <SelectTrigger className="h-7 w-20" data-testid="csat-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </dd>
              </div>
            </dl>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="font-display text-sm font-semibold">Linked Projects</h3>
            <div className="mt-3 space-y-2">
              {client.projects?.length ? client.projects.map((p) => (
                <button key={p.id} onClick={() => navigate(`/pm/projects/${p.id}`)} className="flex w-full items-center gap-2 rounded-lg border border-border p-2 text-left hover:bg-muted">
                  <span className="flex-1 text-sm font-medium">{p.name}</span>
                  <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${p.progress}%` }} /></div>
                  <StatusBadge status={p.status} />
                </button>
              )) : <p className="text-sm text-muted-foreground">No linked projects.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="issues" className="mt-4">
          <div className="mb-3 flex justify-end">
            <FormDialog title="New Issue" testid="new-issue-modal"
              trigger={<Button size="sm" data-testid="new-issue-btn"><Plus className="mr-1 h-3.5 w-3.5" />New Issue</Button>}
              fields={[{ name: "title", label: "Title", required: true, full: true }, { name: "raised_by", label: "Raised By", full: true }]}
              onSubmit={(v) => addIssue.mutateAsync(v)} />
          </div>
          {client.issues?.length ? (
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Title</th><th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Raised By</th><th className="px-4 py-3 font-semibold">Linked Task</th></tr></thead>
                <tbody>
                  {client.issues.map((i) => (
                    <tr key={i.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 font-medium">{i.title}</td>
                      <td className="px-4 py-3"><StatusBadge status={i.status} /></td>
                      <td className="px-4 py-3 text-muted-foreground">{i.raised_by}</td>
                      <td className="px-4 py-3">{i.linked_task ? <span className="flex items-center gap-1 text-primary"><Link2 className="h-3 w-3" />{i.linked_task}</span> : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState icon={Inbox} title="No issues logged" />}
        </TabsContent>

        <TabsContent value="amc" className="mt-4">
          <div className="max-w-md rounded-xl border border-border bg-card p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Annual Maintenance Contract</span>
              <StatusBadge status={amc.status} />
            </div>
            <div className="mt-3 font-display text-3xl font-bold">{money(amc.contract_value)}</div>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Start</dt><dd>{dateFmt(amc.start_date)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">End</dt><dd>{dateFmt(amc.end_date)}</dd></div>
              <div className="flex justify-between items-center"><dt className="text-muted-foreground">Renewal</dt>
                <dd><StatusBadge family={renewFam} label={renewDays == null ? "—" : renewDays < 0 ? "Expired" : `${renewDays} days left`} /></dd></div>
            </dl>
            <FormDialog title="Renew AMC" testid="renew-amc-modal"
              trigger={<Button className="mt-4 w-full" data-testid="renew-amc">Renew Contract</Button>}
              fields={[{ name: "contract_value", label: "New Value", type: "number", required: true }, { name: "end_date", label: "New End Date", type: "date", required: true }]}
              onSubmit={(v) => renewAmc.mutateAsync({ ...v, end_date: new Date(v.end_date).toISOString(), by: client.account_owner_id })} />
          </div>
        </TabsContent>

        <TabsContent value="requests" className="mt-4 space-y-2">
          <div className="flex justify-end">
            <FormDialog title="New Client Request" testid="new-request-modal"
              trigger={<Button size="sm" data-testid="new-request-btn"><Plus className="mr-1 h-3.5 w-3.5" />New Request</Button>}
              fields={[{ name: "body", label: "Request", type: "textarea", required: true }]}
              onSubmit={(v) => addRequest.mutateAsync(v)} />
          </div>
          {client.requests?.length ? client.requests.map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm text-foreground">{r.body}</p>
                <StatusBadge status={r.status} />
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{dateFmt(r.created_at)}</span>
                {r.status === "New" && <Button size="sm" variant="outline" data-testid={`route-${r.id}`}>Route to PM</Button>}
              </div>
            </div>
          )) : <EmptyState icon={Inbox} title="No client requests" />}
        </TabsContent>

        {["Strategic", "Key"].includes(client.account_tier) && (
          <TabsContent value="governance" className="mt-4 space-y-2">
            <div className="flex justify-end">
              <FormDialog title="Log Governance Meeting" testid="new-governance-modal"
                trigger={<Button size="sm" data-testid="new-governance-btn"><Plus className="mr-1 h-3.5 w-3.5" />Log Meeting</Button>}
                fields={[
                  { name: "meeting_type", label: "Meeting Type", type: "select", required: true, options: ["QBR", "Check-in", "Escalation Review", "Renewal Discussion"] },
                  { name: "date", label: "Date", type: "date", required: true },
                  { name: "agenda", label: "Agenda", type: "textarea", full: true },
                  { name: "health_rating", label: "Health Rating", type: "select", options: ["green", "amber", "red"] },
                ]}
                onSubmit={(v) => addGovMeeting.mutateAsync(v)} />
            </div>
            {client.governance_meetings?.length ? client.governance_meetings.map((m) => (
              <div key={m.id} data-testid={`governance-row-${m.id}`} className="group rounded-xl border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className={cn("h-2 w-2 rounded-full", HEALTH_DOT[m.health_rating] || "bg-slate-400")} />
                    <span className="text-sm font-semibold">{m.meeting_type}</span>
                    <span className="text-xs text-muted-foreground">{dateFmt(m.date)}</span>
                  </div>
                  <ConfirmDelete testid={`del-governance-${m.id}`} title="Delete this meeting record?"
                    trigger={<button className="hidden rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-600 group-hover:block"><Trash2 className="h-3.5 w-3.5" /></button>}
                    onConfirm={() => deleteGovMeeting.mutate(m.id)} />
                </div>
                {m.agenda && <p className="mt-2 text-sm text-foreground">{m.agenda}</p>}
                {m.action_items?.length > 0 && (
                  <ul className="mt-2 list-inside list-disc text-xs text-muted-foreground">
                    {m.action_items.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                )}
              </div>
            )) : <EmptyState icon={Inbox} title="No governance meetings logged" />}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
